import React, { useState, useRef, useEffect } from 'react';

import styled from 'styled-components';

import CorePortal from 'shared/CorePortal';
import style from 'styles/styled-components/style';

interface IconDropdownProps {
	children: React.ReactNode;
}

const IconDropdown = ({ children }: IconDropdownProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const triggerRef = useRef<HTMLDivElement>(null);

	const childrenWithProps = React.Children.map(children, (child) => {
		if (React.isValidElement(child)) {
			return React.cloneElement(
				child as React.ReactElement<any>,
				{
					isOpen,
					setIsOpen,
					triggerRef,
				} as Partial<TriggerProps & ViewProps>,
			);
		}
		return child;
	});

	return (
		<Grid flex="center" _height="100%" _width="100%">
			{childrenWithProps}
		</Grid>
	);
};

interface TriggerProps {
	children: React.ReactNode;
	isOpen?: boolean;
	setIsOpen?: (value: boolean) => void;
	triggerRef?: React.RefObject<HTMLDivElement>;
}

const Trigger = ({ children, isOpen, setIsOpen, triggerRef }: TriggerProps) => {
	const handleClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		setIsOpen?.(!isOpen);
	};

	return (
		<Grid ref={triggerRef} flex="center" _height="100%" onClick={handleClick}>
			{children}
		</Grid>
	);
};

interface ViewProps {
	children: React.ReactNode;
	isOpen?: boolean;
	setIsOpen?: (value: boolean) => void;
	triggerRef?: React.RefObject<HTMLDivElement>;
	onClose?: () => void;
}

const View = ({ children, isOpen, setIsOpen, triggerRef, onClose }: ViewProps) => {
	const wrapperRef = useRef<HTMLDivElement | null>(null);
	const [position, setPosition] = useState({ top: 0, left: 0 });
	const [isPositioned, setIsPositioned] = useState(false);

	const handleClose = () => {
		setIsOpen?.(false);
		onClose?.();
	};

	const childrenWithClose = React.Children.map(children, (child) => {
		if (React.isValidElement(child)) {
			return React.cloneElement(
				child as React.ReactElement<any>,
				{
					onClose: handleClose,
				} as { onClose: () => void },
			);
		}
		return child;
	});

	useEffect(() => {
		if (!triggerRef?.current || !isOpen || !wrapperRef.current) {
			setIsPositioned(false);
			return;
		}

		const updatePosition = () => {
			const element = triggerRef.current as HTMLElement;
			const wrapper = wrapperRef.current as HTMLElement;
			const rect = element.getBoundingClientRect();
			const viewportHeight = window.innerHeight;
			const dropdownHeight = wrapper.offsetHeight;
			let topPosition = rect.top;
			if (topPosition + dropdownHeight > viewportHeight) {
				// 위쪽에 공간이 충분한지 확인
				if (rect.top - dropdownHeight > 0) {
					// 위쪽에 표시
					topPosition = rect.top - dropdownHeight;
				} else {
					// viewport 안에 맞도록 조정
					topPosition = viewportHeight - dropdownHeight - 10; // 10px 여유 공간
				}
			}

			setPosition({
				top: topPosition,
				left: rect.left - wrapper.offsetWidth + 30,
			});
			setIsPositioned(true);
		};

		const resizeObserver = new ResizeObserver(updatePosition);
		resizeObserver.observe(wrapperRef.current);
		updatePosition();

		const handleClickOutside = (event: MouseEvent) => {
			const isClickedTarget = triggerRef.current?.contains(event.target as Node);
			const isClickedChildren = wrapperRef.current?.contains(event.target as Node);

			if (isClickedTarget || isClickedChildren) {
				return;
			}

			setIsOpen?.(false);
		};
		document.addEventListener('mousedown', handleClickOutside);

		return () => {
			resizeObserver.disconnect();
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isOpen, setIsOpen]);

	useEffect(() => {
		if (!isOpen) {
			onClose?.();
		}
	}, [isOpen, onClose]);

	if (!isOpen) return null;

	return (
		<CorePortal>
			<WrapperDiv
				ref={wrapperRef}
				style={{
					top: `${position.top}px`,
					left: `${position.left}px`,
					opacity: isPositioned ? 1 : 0,
					transition: 'opacity 0.15s ease-in-out',
				}}
				onClick={(e) => e.stopPropagation()}
			>
				{childrenWithClose}
			</WrapperDiv>
		</CorePortal>
	);
};

IconDropdown.Trigger = Trigger;
IconDropdown.View = View;

export default IconDropdown;

const { Grid } = style;

const WrapperDiv = styled.div`
	position: fixed;
	width: fit-content;
	height: fit-content;
	margin-right: 8px;
	z-index: 100;
	border-radius: 8px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	background-color: ${({ theme }) => theme.colors.gray.gray_white};
`;
